import { useEffect, useRef, useState } from "react";
import type { DeliveryContactDraft } from "@/modules/pos/components/delivery/OffPremiseOrderHead";

/**
 * The own delivery's inline customer fields on the order panel (B6 · P26, the
 * design's `cust` / `custMissing`). The fields are a local draft over the
 * bill's stored contact; leaving a field (or sending) saves what changed with
 * the existing `editCustomer` command — no new endpoint.
 */

type ContactSource = {
  id: string;
  customerName: string | null;
  customerPhone: string | null;
  deliveryAddress: string | null;
};

/** The bill's stored contact as the fields' text. */
export function contactFromInvoice(invoice: ContactSource): DeliveryContactDraft {
  return {
    name: invoice.customerName ?? "",
    phone: invoice.customerPhone ?? "",
    address: invoice.deliveryAddress ?? "",
  };
}

/** Whether the fields say something the bill does not (trimmed, as saved). */
export function contactDirty(draft: DeliveryContactDraft, stored: DeliveryContactDraft): boolean {
  return (
    draft.name.trim() !== stored.name.trim() ||
    draft.phone.trim() !== stored.phone.trim() ||
    draft.address.trim() !== stored.address.trim()
  );
}

/** The design's `custMissing`: the phone or the address is still blank. */
export function contactMissing(draft: DeliveryContactDraft): boolean {
  return !draft.phone.trim() || !draft.address.trim();
}

/**
 * The `editCustomer` payload — all three, each nullable, blank → null: the
 * same shape `CustomerDialog`'s save sends (B107).
 */
export function contactPayload(draft: DeliveryContactDraft): {
  customerName: string | null;
  customerPhone: string | null;
  deliveryAddress: string | null;
} {
  return {
    customerName: draft.name.trim() || null,
    customerPhone: draft.phone.trim() || null,
    deliveryAddress: draft.address.trim() || null,
  };
}

/**
 * The draft, kept in step with the bill: a new bill, or a stored contact that
 * changed (this device's save, another device's correction), refreshes the
 * fields — unless the cashier has typed something not yet saved, which wins.
 */
export function useDeliveryContactDraft(invoice: ContactSource | undefined) {
  const stored = invoice ? contactFromInvoice(invoice) : { name: "", phone: "", address: "" };
  const key = invoice
    ? [invoice.id, stored.name, stored.phone, stored.address].join("\u0000")
    : "";
  const [draft, setDraft] = useState<DeliveryContactDraft>(stored);
  const syncedRef = useRef<{ key: string; value: DeliveryContactDraft }>({ key, value: stored });

  useEffect(() => {
    const previous = syncedRef.current;
    if (previous.key === key) return;
    const sameBill = invoice && previous.key.startsWith(`${invoice.id}\u0000`);
    syncedRef.current = { key, value: stored };
    setDraft((current) =>
      sameBill && contactDirty(current, previous.value) ? current : stored,
    );
    // `stored` is derived from `key`'s parts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return {
    draft,
    stored,
    setField: (field: keyof DeliveryContactDraft, value: string) =>
      setDraft((current) => ({ ...current, [field]: value })),
    dirty: contactDirty(draft, stored),
    missing: contactMissing(draft),
  };
}
